// Encabezado de detalle de un crédito sobre el `DetailHeader` compartido. Cartera lo monta tal cual;
// Cobranza le pasa la cuota seleccionada y el primer dato pasa a ser esa cuota.
import type { ReactNode } from 'react';
import type { LoanInstallmentResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanInstallmentResponse';
import { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import type { LoanDeviceFleetItem, LoanPaymentListItem } from '@/api/services/loan-credit';
import { formatDayMx, mxDay } from '@/lib/dateHelpers';
import {
    installmentArrearsDays,
    installmentDueDay,
    installmentPaidCents,
    lastPaidInstallment,
    nextDueInstallment,
    outstandingBalanceCentsOf,
    paidInstallmentsCountOf,
} from '@/lib/installments';
import {
    DetailHeader,
    DetailHeaderPill,
    type DetailHeaderFact,
} from '@/shared/patterns/DetailHeader';
import { TierPill } from '@/shared/patterns/Pills';
import type { CreditFileBlock } from '../api/useCreditFile';
import { briefFailureNote } from '../failures';
import {
    arrearsDaysLabel,
    creditStatusPillOf,
    dueLineOf,
    lockLineOf,
    resolveArrearsDays,
} from '../detailHeader';
import {
    formatMoneyCents,
    installmentStatusPresentationOf,
    NO_VALUE,
    progressPctOf,
} from '../logic';
import { deviceLockChipOf, saleCustomerLabelOf, saleStoreLabelOf } from '../../installments/hero';
import { paymentOriginFactsOf } from '../../payments/paymentOrigin';
import type { CreditSummaryCredit } from './CreditSummaryTab';
import { productNameOf } from './CreditSummaryTab';
import { CreditGapMark } from './CreditFileUi';

/** Qué dice un dato cuando su bloque no llegó: en vuelo o con su falla. */
function blockNote<T>(block: CreditFileBlock<T>, subject: string): string | null {
    if (block.failure) return briefFailureNote(block.failure, subject);
    if (block.isLoading) return `Cargando ${subject}…`;
    return null;
}

export interface CreditDetailHeaderProps {
    creditId: string;
    /** `undefined` mientras el crédito carga o si su lectura falló. */
    credit: CreditSummaryCredit | undefined;
    /** Por qué no hay crédito: en vuelo o su falla. */
    creditNote?: string | null;
    installments: CreditFileBlock<LoanInstallmentResponse[]>;
    device: CreditFileBlock<LoanDeviceFleetItem | null>;
    today: string;
    /** Cobranza: la cuota abierta. El primer dato deja de ser la próxima cuota y pasa a ser esta. */
    selectedInstallmentNumber?: number;
    /** El pago más reciente del libro (`null` = sin pagos). Con él se agrega el dato «Último pago». */
    lastPayment?: CreditFileBlock<LoanPaymentListItem | null>;
    eyebrow?: ReactNode;
    idAction?: ReactNode;
    ariaLabel?: string;
}

export function CreditDetailHeader({
    creditId,
    credit,
    creditNote = null,
    installments,
    device,
    today,
    selectedInstallmentNumber,
    lastPayment,
    eyebrow,
    idAction,
    ariaLabel,
}: CreditDetailHeaderProps) {
    const plan = installments.data;
    const planNote = blockNote(installments, 'el plan de pagos');
    const sale = credit?.saleContext;
    const customer = sale ? saleCustomerLabelOf(sale) : null;

    const arrears = resolveArrearsDays(credit?.overdueDays, plan, today);
    const status = credit ? creditStatusPillOf(credit.status, arrears) : null;
    const level = credit?.clientLevelAtOrigination;

    const name =
        customer === null ? (
            NO_VALUE
        ) : 'name' in customer ? (
            customer.name
        ) : (
            <CreditGapMark note={customer.note} label={customer.missing} />
        );

    const firstFact =
        selectedInstallmentNumber === undefined
            ? nextInstallmentFact(plan, planNote, today)
            : selectedInstallmentFact(plan, planNote, selectedInstallmentNumber, today);

    const closed = credit ? closedFactsOf(credit, plan, planNote, lastPayment?.data) : null;
    const facts: DetailHeaderFact[] = [
        closed && selectedInstallmentNumber === undefined ? closed.first : firstFact,
        closed ? closed.second : balanceFact(credit, plan, planNote),
        equipmentFact(credit, device),
        ...(lastPayment ? [lastPaymentFact(lastPayment)] : []),
    ];

    return (
        <DetailHeader
            ariaLabel={ariaLabel}
            eyebrow={eyebrow}
            name={name}
            badges={
                <>
                    {level ? <TierPill level={level} label="full" /> : null}
                    {status ? (
                        <DetailHeaderPill tone={status.tone} title={status.title}>
                            {status.label}
                        </DetailHeaderPill>
                    ) : null}
                </>
            }
            subItems={[
                sale ? saleStoreLabelOf(sale) : undefined,
                credit ? `originado ${formatDayMx(mxDay(credit.createdAt))}` : undefined,
            ]}
            idLabel="Crédito"
            idValue={creditId}
            idAction={idAction}
            note={creditNote}
            facts={facts}
        />
    );
}

/** «20 de 20 cuotas pagadas», con las cifras del crédito. */
function paidLineOf(credit: CreditSummaryCredit): string {
    return `${String(credit.paidInstallments)} de ${String(credit.termWeeks)} cuotas pagadas`;
}

/**
 * Un crédito cerrado no tiene próxima cuota ni saldo que cobrar: los dos primeros datos dicen cómo
 * terminó. `null` = sigue vivo (o en baja contable, que todavía debe) y lleva los datos de cobro.
 */
function closedFactsOf(
    credit: CreditSummaryCredit,
    plan: readonly LoanInstallmentResponse[] | undefined,
    planNote: string | null,
    lastPayment: LoanPaymentListItem | null | undefined,
): { first: DetailHeaderFact; second: DetailHeaderFact } | null {
    const changedOn = formatDayMx(mxDay(credit.updatedAt));
    if (credit.status === LoanCreditStatusEnum.SETTLED) {
        // Con el libro de pagos manda el último pago real; sin él, la última cuota cobrada del plan.
        const paidAt = lastPayment
            ? lastPayment.appliedAt
            : plan
              ? lastPaidInstallment(plan)?.paidAt
              : null;
        return {
            first: {
                key: 'settled',
                label: 'Liquidado',
                title: 'Fecha del último pago',
                value: paidAt ? formatDayMx(mxDay(paidAt)) : (planNote ?? 'Sin fecha de pago'),
                sub: paidLineOf(credit),
            },
            second: totalPaidFact(credit, plan, planNote),
        };
    }
    if (credit.status === LoanCreditStatusEnum.CANCELLED) {
        return {
            first: {
                key: 'cancelled',
                label: 'Cancelado',
                title: 'Fecha del último cambio del crédito',
                value: changedOn,
                sub: 'Sin cuotas por cobrar',
            },
            second: {
                key: 'down-payment',
                label: 'Enganche',
                value: formatMoneyCents(credit.downPaymentCents),
                sub: 'Se devuelve al cancelar',
            },
        };
    }
    if (credit.status === LoanCreditStatusEnum.DISCARDED) {
        return {
            first: {
                key: 'discarded',
                label: 'Descartado',
                title: 'Fecha del último cambio del crédito',
                value: changedOn,
                sub: 'Nunca se activó; sin cuotas por cobrar',
            },
            second: {
                key: 'financed',
                label: 'Monto a pagar',
                value: formatMoneyCents(credit.totalToPayCents),
                sub: 'No llegó a cobrarse',
            },
        };
    }
    return null;
}

/** «03/oct/2026 · Tienda Limpia»: cuándo y dónde fue el último pago del libro. */
function lastPaymentFact(block: CreditFileBlock<LoanPaymentListItem | null>): DetailHeaderFact {
    const base = { key: 'last-payment', label: 'Último pago' };
    const note = blockNote(block, 'los pagos');
    if (block.data === undefined) return { ...base, value: NO_VALUE, sub: note ?? undefined };
    if (block.data === null) return { ...base, value: 'Todavía no hay pagos' };
    const { where } = paymentOriginFactsOf(block.data);
    return { ...base, value: `${formatDayMx(mxDay(block.data.appliedAt))} · ${where}` };
}

/** Lo cobrado según el plan (cuotas pagadas); el enganche va aparte. */
function totalPaidFact(
    credit: CreditSummaryCredit,
    plan: readonly LoanInstallmentResponse[] | undefined,
    planNote: string | null,
): DetailHeaderFact {
    const base = {
        key: 'total-paid',
        label: 'Total pagado',
        title: 'Suma de las cuotas cobradas del plan; el enganche va aparte',
    };
    if (!plan) return { ...base, value: planNote ?? 'Sin plan de pagos' };
    const paid = plan.reduce((total, item) => total + (installmentPaidCents(item) ?? 0), 0);
    return {
        ...base,
        value: formatMoneyCents(paid),
        sub: `Más enganche de ${formatMoneyCents(credit.downPaymentCents)}`,
    };
}

function nextInstallmentFact(
    plan: readonly LoanInstallmentResponse[] | undefined,
    planNote: string | null,
    today: string,
): DetailHeaderFact {
    const base = { key: 'next', label: 'Próxima cuota' };
    if (!plan) return { ...base, value: NO_VALUE, sub: planNote ?? undefined };
    const next = nextDueInstallment(plan);
    if (next === null) return { ...base, value: NO_VALUE, sub: 'Sin cuotas pendientes' };
    const overdue = (installmentArrearsDays(next, today) ?? 0) > 0;
    return {
        ...base,
        value: formatMoneyCents(next.totalCents),
        sub: next.dueDate
            ? dueLineOf(installmentDueDay(next.dueDate), today)
            : 'Se fecha al activar',
        ...(overdue && { tone: 'danger' as const }),
    };
}

function selectedInstallmentFact(
    plan: readonly LoanInstallmentResponse[] | undefined,
    planNote: string | null,
    installmentNumber: number,
    today: string,
): DetailHeaderFact {
    const base = { key: 'selected', label: `Cuota ${String(installmentNumber)}` };
    if (!plan) return { ...base, value: NO_VALUE, sub: planNote ?? undefined };
    const row = plan.find((item) => item.installmentNumber === installmentNumber);
    if (!row) return { ...base, value: NO_VALUE, sub: 'No aparece en el plan del crédito' };
    const arrears = installmentArrearsDays(row, today);
    const overdue = arrears !== null && arrears > 0;
    const statusText = overdue
        ? arrearsDaysLabel(arrears)
        : installmentStatusPresentationOf(row.status).label;
    const due = row.dueDate ? `vence ${formatDayMx(installmentDueDay(row.dueDate))}` : 'Sin fecha';
    return {
        ...base,
        value: formatMoneyCents(row.totalCents),
        sub: `${due} · ${statusText}`,
        ...(overdue && { tone: 'danger' as const }),
    };
}

/**
 * Con la deuda total publicada, es la cifra principal y el saldo insoluto (capital) va debajo. Sin
 * ella queda el saldo insoluto, con su etiqueta de siempre.
 */
function balanceFact(
    credit: CreditSummaryCredit | undefined,
    plan: readonly LoanInstallmentResponse[] | undefined,
    planNote: string | null,
): DetailHeaderFact {
    const outstanding =
        credit?.outstandingBalanceCents ?? (plan ? outstandingBalanceCentsOf(plan) : null);
    const outstandingLabel = outstanding === null ? NO_VALUE : formatMoneyCents(outstanding);
    const totalDebt = credit?.totalDebtCents;
    const base =
        typeof totalDebt === 'number'
            ? {
                  key: 'balance',
                  label: 'Deuda total',
                  title: 'Capital, interés e IVA que faltan de las cuotas abiertas',
                  value: formatMoneyCents(totalDebt),
              }
            : { key: 'balance', label: 'Saldo insoluto', value: outstandingLabel };
    const capitalLine =
        typeof totalDebt === 'number' ? `Saldo insoluto ${outstandingLabel}` : undefined;
    const paid = credit?.paidInstallments ?? (plan ? paidInstallmentsCountOf(plan) : null);
    const total = credit?.termWeeks ?? plan?.length;
    if (paid === null || total === undefined) {
        const sub = [capitalLine, planNote ?? undefined].filter(Boolean).join(' · ');
        return { ...base, sub: sub === '' ? undefined : sub };
    }
    const paidLine = `${String(paid)} de ${String(total)} cuotas pagadas`;
    return {
        ...base,
        sub: capitalLine ? `${capitalLine} · ${paidLine}` : paidLine,
        progressPct: progressPctOf(paid, total),
    };
}

function equipmentFact(
    credit: CreditSummaryCredit | undefined,
    device: CreditFileBlock<LoanDeviceFleetItem | null>,
): DetailHeaderFact {
    const base = { key: 'device', label: 'Equipo' };
    if (device.data === null) {
        return {
            ...base,
            value: credit ? productNameOf(credit) : NO_VALUE,
            sub: 'Sin dispositivo',
        };
    }
    // En vuelo o caído, el candado se dice con su nota: «desconocido» no explica por qué.
    const lockNote = blockNote(device, 'el expediente MDM');
    const lock = lockNote === null ? deviceLockChipOf(device) : null;
    const imei = credit?.imei ?? device.data?.imei;
    const parts = [
        imei ? `IMEI ${imei}` : undefined,
        lock ? lockLineOf(lock.label) : (lockNote ?? undefined),
    ].filter((part): part is string => part !== undefined);
    return {
        ...base,
        value: credit ? productNameOf(credit) : NO_VALUE,
        sub: parts.length > 0 ? parts.join(' · ') : undefined,
    };
}
